import { NavigationRules } from '../types/navigation';

export default class NavigationService {
  private static STORAGE_KEY = 'navigationRules';
  private static DEFAULT_RULES: NavigationRules = {
    Dashboard: {
      name: "dashboard",
      icon: "mdi-view-dashboard",
      translationKey: "dashboard.label",
      isEditable: false,
      permissionRequired: [
        { domain: "Dashboard", permisions: ["Read"] },
      ],
    },
    Modules: {
      icon: "mdi-drawing-box",
      translationKey: "menu.modules",
      isEditable: false,
      permissionRequired: [
        { domain: "Record", permisions: ["Read"] },
        { domain: "Document", permisions: ["Read"] },
        { domain: "Survey", permisions: ["Read"] },
        { domain: "TokenWallet", permisions: ["Read"] },
      ],
      subMenu: {
        Record: {
          name: "records",
          module: "Record",
          icon: "mdi-calendar-multiple-check",
          translationKey: "records.label",
          isEditable: false,      
          permissionRequired: [
            { domain: "Record", permisions: ["Read"] },
          ],
        },
        Documents: {
          name: "documents",
          module: "Document",
          icon: "mdi-file",
          translationKey: "documents.label",
          isEditable: false,
          permissionRequired: [
            { domain: "Document", permisions: ["Read"] },
          ],
        },
        Inspections: {
          name: "inspections",
          module: "Inspection",
          icon: "mdi-order-bool-ascending-variant",
          translationKey: "inspections.label",
          isEditable: false,
          permissionRequired: [
            { domain: "Survey", permisions: ["Read"] },
          ],
        },
        Tokens: {
          module: "Tokens",
          icon: "mdi-poker-chip",
          translationKey: "tokens.label",
          isEditable: false,
          permissionRequired: [
            { domain: "TokenStock", permisions: ["Read"] },
            { domain: "TokenWallet", permisions: ["Read"] },
            { domain: "TokenContract", permisions: ["Read"] },
          ],
          subMenu: {
            Stock: {
              name: "token-stock",
              show: false,
              permissionRequired: [
                { domain: "TokenStock", permisions: ["Read"] },
              ],
              module: "TokenStock",
              icon: "mdi-archive",
              translationKey: "tokens.menu.tokenStock",
              isEditable: false,
            },
            Wallet: {
              name: "token-wallet",
              show: false,
              permissionRequired: [
                { domain: "TokenWallet", permisions: ["Read"] },
              ],
              module: "Wallet",
              icon: "mdi-credit-card",
              translationKey: "tokens.menu.wallet",
              isEditable: false,
            },
            Contracts: {
              name: "token-contracts",
              show: false,
              permissionRequired: [
                { domain: "TokenContract", permisions: ["Read"] },
              ],
              module: "Contracts",
              icon: "mdi-database",
              translationKey: "tokens.menu.contracts",
              isEditable: false,
            },
            FAQ: {
              name: "token-faq",
              show: false,
              permissionRequired: [
                { domain: "TokenWallet", permisions: ["Read"] },
              ],
              module: "FAQ",
              icon: "mdi-frequently-asked-questions",
              translationKey: "tokens.menu.faq",
              isEditable: false,
            }
          }
        }
      }
    },
    Reports: {
      icon: "mdi-cloud-print-outline",
      translationKey: "reports.label",
      isEditable: false,
      permissionRequired: [
        { domain: "StockReport", permisions: ["Read"] },
        { domain: "Traceability", permisions: ["Read"] },
        { domain: "GeneralReport", permisions: ["Read"] },
        { domain: "BiorganicReport", permisions: ["Read"] },
        { domain: "TraceabilityV2Report", permisions: ["Read"] },
      ],
      subMenu: {
        Traceability: {
          name: "traceability-view",
          show: false,
          permissionRequired: [
            { domain: "Traceability", permisions: ["Read"] },
          ],
          module: "Traceability",
          icon: "mdi-transit-detour",
          translationKey: "reports.menu.traceability-report",
          isEditable: false,
        },
        CustomReports: {
          name: "custom-reports", // keep the same route name for backward compatibility
          show: false,
          permissionRequired: [
            { domain: "CustomReport", permisions: ["Read"] },
          ],
          module: "CustomReport", // this could be updated if the backend api is also updated
          icon: "mdi-file-document-outline",
          translationKey: "reports.menu.custom-report",
          isEditable: false,
        },
        Stock: {
          name: "stock-report",
          show: false,
          permissionRequired: [
            { domain: "StockReport", permisions: ["Read"] },
          ],
          module: "Stock",
          icon: "mdi-inbox-multiple-outline",
          translationKey: "reports.menu.stock-report",
          isEditable: false,
        },
        General: {
          name: "general-report",
          show: false,
          permissionRequired: [
            { domain: "GeneralReport", permisions: ["Read"] },
          ],
          module: "General",
          icon: "mdi-chart-donut-variant",
          translationKey: "reports.menu.general-report",
          isEditable: false,
        },
        Biorganic: {
          name: "biorganic-report",
          show: false,
          permissionRequired: [
            { domain: "BiorganicReport", permisions: ["Read"] },
          ],
          module: "BiorganicReport",
          icon: "mdi-account-heart",
          translationKey: "reports.menu.biorganic-report",
          isEditable: false,
        }
      }
    },
    Integrations: {
      name: "integrations",
      path: "/integrations",
      icon: "mdi-webhook",
      translationKey: "integrations.label",
      isEditable: false,
      permissionRequired: [
        { domain: "AbrapaProductAPI", permisions: ["Write"] },
        { domain: "AbrapaOrderAPI", permisions: ["Write"] },
      ],
    },
    Entries: {
      icon: "mdi-book-plus",
      translationKey: "menu.entries",
      isEditable: false,
      permissionRequired: [
        { domain: "Location", permisions: ["Read"] },
        { domain: "StorageDevice", permisions: ["Read"] },
        { domain: "Culture", permisions: ["Read"] },
        { domain: "Season", permisions: ["Read"] },
        { domain: "Input", permisions: ["Read"] },
        { domain: "Operator", permisions: ["Read"] },
        { domain: "Equipment", permisions: ["Read"] },
      ],
      subMenu: {
        Location: {
          name: "locations",
          module: "Location",
          icon: "mdi-barn",
          translationKey: "locations.label",
          isEditable: false,
          permissionRequired: [
            { domain: "Location", permisions: ["Read"] },
          ],
        },
        StorageDevice: {
          name: "storage",
          module: "StorageDevice",
          icon: "mdi-map-marker-radius",
          translationKey: "storage.label",
          isEditable: false,
          permissionRequired: [
            { domain: "StorageDevice", permisions: ["Read"] },
          ],
        },
        Culture: {
          name: "products",
          module: "Culture",
          icon: "mdi-package-variant-closed",
          translationKey: "product.label",
          isEditable: false,
          permissionRequired: [
            { domain: "Culture", permisions: ["Read"] },
          ],
        },
        Season: {
          name: "seasons",
          module: "Season",
          icon: "mdi-barley",
          translationKey: "season.label",
          isEditable: false,
          permissionRequired: [
            { domain: "Season", permisions: ["Read"] },
          ],
        },
        Input: {
          name: "inputs",
          module: "Input",
          icon: "mdi-sack",
          translationKey: "input.label",
          isEditable: false,
          permissionRequired: [
            { domain: "Input", permisions: ["Read"] },
          ],
        },
        Operator: {
          name: "operators",
          module: "Operator",
          icon: "mdi-account-hard-hat",
          translationKey: "operator.label",
          isEditable: false,
          permissionRequired: [
            { domain: "Operator", permisions: ["Read"] },
          ],
        },
        Producer: {
          name: "producers",
          module: "Producer",
          icon: "mdi-account-settings",
          translationKey: "producer.label",
          isEditable: false,
          permissionRequired: [
            { domain: "Producer", permisions: ["Read"] },
          ],
        },
        Equipment: {
          name: "equipments",
          module: "Equipment",
          icon: "mdi-tractor",
          translationKey: "equipment.label",
          isEditable: false,
          permissionRequired: [
            { domain: "Equipment", permisions: ["Read"] },
          ],
        }
      }
    },
    Configuration: {
      icon: "mdi-cog",
      translationKey: "menu.configuration",
      isEditable: false,
      permissionRequired: [
        { domain: "Company", permisions: ["Read"] },
        { domain: "StorageDeviceType", permisions: ["Read"] },
        { domain: "Structure", permisions: ["Read"] },
        { domain: "Process", permisions: ["Read"] },
        { domain: "PreRecord", permisions: ["Read"] },
        { domain: "surveyAdm", permisions: ["Read"] },
        { domain: "PersonInCharge", permisions: ["Read"] },
        { domain: "Role", permisions: ["Read"] },
        { domain: "Translations", permisions: ["Read"] },
      ],
      subMenu: {
        Company: {
          name: "companies",
          module: "Company",
          icon: "mdi-briefcase",
          translationKey: "company.label",
          isEditable: false,
          permissionRequired: [
            { domain: "Company", permisions: ["Read"] },
          ],
        },
        StorageDeviceType: {
          name: "storage-types",
          module: "StorageDeviceType",
          icon: "mdi-database-plus",
          translationKey: "storageType.label",
          isEditable: false,
          permissionRequired: [
            { domain: "StorageDeviceType", permisions: ["Read"] },
          ],
        },
        Structure: {
          name: "details",
          module: "Structure",
          icon: "mdi-text-box-plus-outline",
          translationKey: "structure.label",
          isEditable: false,
          permissionRequired: [
            { domain: "Structure", permisions: ["Read"] },
          ],
        },
        Process: {
          name: "proccess",
          module: "Process",
          icon: "mdi-all-inclusive",
          translationKey: "process.label",
          isEditable: false,
          permissionRequired: [
            { domain: "Process", permisions: ["Read"] },
          ],
        },
        PreRecord: {
          name: "prerecords",
          module: "PreRecord",
          icon: "mdi-calendar-multiple-check",
          translationKey: "pre-records.label",
          isEditable: false,
          permissionRequired: [
            { domain: "PreRecord", permisions: ["Read"] },
          ],
        },
        SurveyAdm: {
          name: "survey",
          module: "survey",
          icon: "mdi-order-bool-ascending-variant",
          translationKey: "survey.label",
          isEditable: false,
          permissionRequired: [
            { domain: "surveyAdm", permisions: ["Read"] },
          ],
        },
        PersonInCharge: {
          name: "authority",
          module: "PersonInCharge",
          icon: "mdi-account-key",
          translationKey: "authority.label",
          isEditable: false,
          permissionRequired: [
            { domain: "PersonInCharge", permisions: ["Read"] },
          ],
        },
        Role: {
          name: "roles",
          module: "Role",
          icon: "mdi-shield-account",
          translationKey: "roles.label",
          isEditable: false,
          permissionRequired: [
            { domain: "Role", permisions: ["Read"] },
          ],
        },
        Translations: {
          name: "translations",
          module: "Translations",
          icon: "mdi-translate-variant",
          translationKey: "translations.label",
          isEditable: false,
          permissionRequired: [
            { domain: "Translations", permisions: ["Read"] },
          ],
        },
        // MenuManager: {
        //   name: "menu-manager",
        //   module: "Configuration",
        //   icon: "mdi-menu",
        //   translationKey: "menu.manager.title",
        //   isEditable: false,
        //   permissionRequired: [
        //     { domain: "Translations", permisions: ["Write"] }
        //   ],
        // }
      }
    }
  };

  static getNavigationRules(): NavigationRules {
    try {
      const savedRules = localStorage.getItem(this.STORAGE_KEY);
      return savedRules ? JSON.parse(savedRules) : this.DEFAULT_RULES;
    } catch (error) {
      console.error('Error loading navigation rules:', error);
      return this.DEFAULT_RULES;
    }
  }

  static saveNavigationRules(rules: NavigationRules): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(rules));
      // Emitir un evento para notificar cambios
      window.dispatchEvent(new Event('navigation-rules-updated'));
    } catch (error) {
      console.error('Error saving navigation rules:', error);
      throw new Error('Failed to save navigation rules');
    }
  }

  static updateMenuOrder(orderedItems: NavigationRules): void {
    try {
      const currentRules = this.getNavigationRules();
      const updatedRules = {
        ...currentRules,
        ...orderedItems
      };
      this.saveNavigationRules(updatedRules);
    } catch (error) {
      console.error('Error updating menu order:', error);
      throw new Error('Failed to update menu order');
    }
  }
}
